import { keepPreviousData } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Select, SelectItem, Small } from "@fronterahealth/frontera-ui-components";

import { ReportTypeEnums, useGetProviderLearnersQuery, useGetReportsQuery } from "@api/graphql/types-and-hooks";

interface SelectClientTabProps {
  reportType: ReportTypeEnums;
  existingReportOpened: () => void;
  setLearnerId: (learnerId: string) => void;
  setOperationsDisabled: (operationsDisabled: boolean) => void;
}

export const SelectClientTab: React.FC<SelectClientTabProps> = ({
  reportType,
  existingReportOpened,
  setLearnerId,
  setOperationsDisabled,
}) => {
  const [learnerName, setLearnerName] = useState<string>("");
  const [selected, setSelected] = useState<SelectItem>();

  const setSelectedWraper = (item: SelectItem | undefined) => {
    setSelected(item);
    setLearnerId(item?.id ?? "");
  };
  const go = useNavigate();

  const learnerQuery = useGetProviderLearnersQuery(
    { learnerName },
    {
      queryKey: ["get-provider-learners", learnerName],
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      placeholderData: keepPreviousData,
    },
  );
  const { data, isLoading } = learnerQuery;

  const getLearnerAssessment = useGetReportsQuery(
    {
      filter: {
        learnerId: selected?.id,
        reportType,
      },
    },
    {
      queryKey: ["get-learner-assessment", selected?.id],
      enabled: false,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );
  const { data: learnerAssessmentData, isLoading: learnerAssessmentLoading } = getLearnerAssessment;

  useEffect(() => {
    const validateObject = async () => {
      await getLearnerAssessment.refetch();
    };

    if (selected) {
      validateObject();
    }
  }, [selected]);

  const goToReport = () => {
    existingReportOpened();
    go(`/assessment-reports/${learnerAssessmentData?.getReports?.edges[0]?.node?.id}`, {
      state: {
        id: learnerAssessmentData?.getReports?.edges[0]?.node?.id,
        clientName: selected?.primary,
        lastUpdated: new Date(learnerAssessmentData?.getReports?.edges[0]?.node?.lastUpdatedDate),
      },
    });
  };

  useEffect(() => {
    if (selected && !learnerAssessmentData?.getReports?.edges[0]?.node) {
      setOperationsDisabled(false);
    } else {
      setOperationsDisabled(true);
    }
  }, [learnerAssessmentData]);

  const itemsData = useMemo(() => {
    const items: SelectItem[] = [];

    if (data?.getProviderLearners) {
      const learnerItems = data.getProviderLearners?.edges
        .map((learner) => ({
          primary: `${learner?.node?.learnermetadata?.firstName} ${learner?.node?.learnermetadata?.lastName}`,
          id: learner?.node?.id,
          className: "ph-no-capture",
        }))
        .sort((la, lb) => (la.primary > lb.primary ? 1 : -1));
      return learnerItems;
    }

    return items;
  }, [data]);

  return (
    <>
      <Select
        title="Select Client"
        isLoading={isLoading}
        items={itemsData}
        placeholderText="Select a client"
        selected={selected}
        setSelected={(val) => {
          setSelectedWraper(val);
          if (val?.id) {
            setLearnerName("");
          }
        }}
        enableSearch
        searchValue={learnerName}
        onSearch={(inputValue: string) => setLearnerName(inputValue)}
        searchDebounceMs={400}
      />
      {learnerAssessmentLoading && <Small>Loading...</Small>}
      {learnerAssessmentData?.getReports?.edges[0]?.node ? (
        <>
          <Small displayType="normal" className="-mt-6">
            An assessment for this client already exists.
            <br /> Click{" "}
            <span className="cursor-pointer text-secondary underline" onClick={() => goToReport()}>
              here
            </span>{" "}
            to view
          </Small>
        </>
      ) : (
        <></>
      )}
    </>
  );
};
